import Api from '../api'
import { getToken, setToken, removeToken } from './cookie'
import { getLocationOrigin } from './util'

export const MCP_REQUEST_OPTIONS = { manualAuth: true, isLoading: false, notMsg: true }
const RESUME_KEY = 'mcp-oauth:login-resume'
const REQUIRED_PARAMS = ['response_type', 'client_id', 'redirect_uri', 'resource',
  'scope', 'code_challenge', 'code_challenge_method']

export function readOAuthParams(search) {
  const query = new URLSearchParams(search)
  const params = {}
  for (const name of [...REQUIRED_PARAMS, 'state']) {
    const values = query.getAll(name)
    if (values.length > 1 || (name !== 'state' && !values[0])) {
      throw new Error('授权链接参数缺失或重复，请从 WorkBuddy 重新发起。')
    }
    if (values.length) params[name] = values[0]
  }
  return params
}

export function isMcpAuthorizationUrl(value) {
  try {
    const url = new URL(value)
    const allowed = new URL(getLocationOrigin() + '/mcpLogin')
    return url.origin === allowed.origin && url.pathname === allowed.pathname
      && !url.username && !url.password && !url.hash
  } catch (_) {
    return false
  }
}

export function saveMcpLoginResume(url) {
  if (!isMcpAuthorizationUrl(url)) throw new Error('登录恢复地址无效，请重新发起授权。')
  readOAuthParams(new URL(url).search)
  window.sessionStorage.setItem(RESUME_KEY, url)
}

export function getMcpLoginResume() {
  const url = window.sessionStorage.getItem(RESUME_KEY)
  if (!url) return null
  if (!isMcpAuthorizationUrl(url)) {
    window.sessionStorage.removeItem(RESUME_KEY)
    return null
  }
  return url
}

export function clearMcpLoginResume() {
  window.sessionStorage.removeItem(RESUME_KEY)
}

export function readMcpContext() {
  if (!isMcpAuthorizationUrl(window.location.href)) throw new Error('授权页面地址无效。')
  const params = readOAuthParams(window.location.search)
  const query = new URLSearchParams(window.location.search)
  const tickets = query.getAll('ticket')
  const codes = query.getAll('code')
  const resume = getMcpLoginResume()
  const matchesResume = resume && JSON.stringify(readOAuthParams(new URL(resume).search)) === JSON.stringify(params)
  if (tickets.length && (tickets.length !== 1 || !tickets[0] || !matchesResume)) {
    throw new Error('登录回调缺少匹配的授权上下文，请重新登录。')
  }
  if (codes.length && (codes.length !== 1 || !codes[0] || !matchesResume)) {
    throw new Error('登录回调缺少匹配的授权上下文，请重新登录。')
  }
  // Ticket 回调恢复原字符串，不重建 OAuth query，也不恢复自动同意意图。
  return { params, originalUrl: matchesResume ? resume : window.location.href, ticket: tickets[0], loginCode: codes[0] }
}

export function isCurrentMcpRequest(context) {
  try {
    return isMcpAuthorizationUrl(window.location.href)
      && JSON.stringify(readOAuthParams(window.location.search)) === JSON.stringify(context.params)
  } catch (_) {
    return false
  }
}

export function mainSessionFromToken(token) {
  if (typeof token !== 'string') return null
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(window.atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '=')))
    // 仅用于选择 Realm 和隔离同用户重试；签名、有效期及 JTI 仍由后端校验。
    if (claims.login_realm !== 'main' || claims.token_type !== 'access'
        || typeof claims.user_id !== 'string' || !claims.user_id) return null
    return { token, userId: claims.user_id }
  } catch (_) {
    return null
  }
}

export function readMainSession(prefix) {
  return mainSessionFromToken(getToken(prefix))
}

// 比较原始 Token，空会话及同用户的新 Token 也能识别；不依赖跨标签页事件。
export function captureMainSession(prefix) {
  return { token: getToken(prefix) }
}

export function isMainSessionCurrent(snapshot, prefix) {
  return Boolean(snapshot) && getToken(prefix) === snapshot.token
}

export function clearMainSession(prefix, snapshot) {
  if (!isMainSessionCurrent(snapshot, prefix)) return false
  removeToken(prefix)
  return true
}

export function saveMainSession(session, prefix, snapshot) {
  if (!isMainSessionCurrent(snapshot, prefix)) return false
  setToken(session.token, prefix)
  return true
}

export async function loadMcpMainPrefix() {
  const res = await Api.getPrdConfig('SYS', MCP_REQUEST_OPTIONS)
  if (res?.data?.code !== 0 || typeof res.data.data?.prefix !== 'string') {
    throw new Error('无法取得 Main 登录配置，请稍后重新打开页面。')
  }
  return res.data.data.prefix
}

export async function resolveMcpLogin(data, originalUrl) {
  let result = data?.token_response || data
  if (data?.ticket || data?.loginCode) {
    let res
    try { res = await Api.exchangeMcpTicket(data.ticket, originalUrl, data.loginCode) }
    catch (_) { throw new Error('登录凭证兑换请求失败，请重新登录。') }
    if (res?.data?.code !== 0) throw new Error('登录凭证兑换失败，请重新登录。')
    result = res.data.data
    if (result?.status !== 'login' && result?.status !== 'redirect') {
      throw new Error('登录凭证已失效，请重新登录。')
    }
  }
  if (result?.status === 'redirect' && typeof result.login_url === 'string' && result.login_url) {
    return { loginUrl: result.login_url }
  }
  if (result?.status && result.status !== 'login') throw new Error('登录失败，请重新登录。')
  const session = mainSessionFromToken(result?.token)
  if (!session) throw new Error('未取得 Main 登录凭据，请使用系统登录重新登录。')
  return { session }
}

export async function getMcpApprovalRecord(originalUrl, userId) {
  if (!window.crypto?.randomUUID || !window.crypto?.subtle) {
    throw new Error('浏览器不支持安全授权，请使用 HTTPS 和新版浏览器。')
  }
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify([originalUrl, userId])))
  const hash = Array.from(new Uint8Array(digest), n => n.toString(16).padStart(2, '0')).join('')
  const storageKey = `mcp-oauth:approval:${hash}`
  const saved = window.sessionStorage.getItem(storageKey)
  const record = saved ? JSON.parse(saved) : { key: window.crypto.randomUUID(), completed: false }
  if (typeof record.key !== 'string' || !/^[A-Za-z0-9._~-]{16,128}$/.test(record.key) || typeof record.completed !== 'boolean') {
    throw new Error('授权重试记录异常，请从 WorkBuddy 重新发起。')
  }
  window.sessionStorage.setItem(storageKey, JSON.stringify(record))
  return { ...record, storageKey }
}

export function completeMcpApproval(record) {
  window.sessionStorage.setItem(record.storageKey, JSON.stringify({ key: record.key, completed: true }))
}

export function mcpAuthorizationError(code) {
  const messages = {
    1: '授权参数异常，或本次授权已完成、过期或发生幂等冲突。请从 WorkBuddy 重新发起。',
    2: '授权服务内部错误，结果可能未知，请稍后用原请求重试。',
    3: 'Main 登录已失效，请重新登录后再授权。',
    4: '授权服务权限配置异常，请联系管理员。',
    11000: 'OAuth 客户端无效，请从 WorkBuddy 重新发起或核对客户端注册。',
    11001: 'OAuth 资源未获准，请核对客户端请求和服务端配置。',
    11002: 'OAuth 授权范围无效，请从 WorkBuddy 重新发起。',
    11003: '当前凭据不是 Main 登录态，请使用系统登录重新登录。',
    11005: '个人版企业或员工已删除，请联系管理员。',
    11006: '个人版授权主体已禁用或失效，请联系管理员。',
    11007: '存在重复个人版关系，请联系管理员。',
    11008: '个人版系统模板不可用，请联系管理员。',
  }
  return messages[code] || '授权服务响应异常，结果可能未知，请用原请求重试。'
}
